import React, { useState, useEffect, useRef } from 'react';
import { FaBullseye, FaUserTie, FaMobileAlt, FaStar, FaCalendarAlt, FaCheckCircle, FaTimes, FaClock, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase/config';
import { collection, addDoc, updateDoc, doc } from 'firebase/firestore';
import Loader from '../components/Loader';
import './BespokeConsultation.css';
/* eslint-disable react-hooks/exhaustive-deps */

const BespokeConsultation = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    consultationType: '',
    slot: '',
    message: ''
  });

  const [isProcessing, setIsProcessing] = useState(false);
  const [bookingId, setBookingId] = useState(null);
  const [showCalendlyModal, setShowCalendlyModal] = useState(false);
  const [showSlotSuccessModal, setShowSlotSuccessModal] = useState(false);
  const [showFullLoader, setShowFullLoader] = useState(false);
  const [validationError, setValidationError] = useState(null);
  
  // Slot selection states
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [showSlotSelection, setShowSlotSelection] = useState(false);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  
  // Use refs to preserve data for Calendly events
  const bookingIdRef = useRef(null);
  const formDataRef = useRef({});

  // Fetch available slots from Calendly API
  const fetchAvailableSlots = async () => {
    if (!formData.consultationType) {
      return;
    }
    
    setLoadingSlots(true);
    
    try {
      // Get event type UUID based on consultation type
      const eventTypeUuid = formData.consultationType === 'video' 
        ? 'c573f00a-c7d1-4cfd-b9ad-c9396ef258e8' // Video Consultation Vastre By Vijh
        : '1f44e7f6-6046-47f5-ace7-4ee9922da4cb'; // Phone Consultation
      
      const requestData = {
        eventTypeUuid: eventTypeUuid,
        username: 'vastrebyvijh'
      };
      
      // Add timeout to prevent long loading
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout
      
      const response = await fetch(
        "https://us-central1-vastrebyvijh-8c454.cloudfunctions.net/fetchCalendlyAvailability",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            // No Authorization header needed - slots can be viewed without login
          },
          body: JSON.stringify(requestData),
          signal: controller.signal
        }
      );

      clearTimeout(timeoutId);
      const result = await response.json();

      if (result.success) {
        // Transform Calendly slots to our format
        const slots = result.slots.map(slot => ({
          id: slot.start_time,
          startTime: slot.start_time,
          endTime: slot.end_time,
          date: new Date(slot.start_time).toLocaleDateString('en-US', { 
            weekday: 'short', 
            month: 'short', 
            day: 'numeric' 
          }),
          time: new Date(slot.start_time).toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
          }),
          available: slot.status === 'available'
        }));
        
        setAvailableSlots(slots);
      } else {
        // Fallback to mock slots if API fails
        const mockSlots = generateMockSlots();
        setAvailableSlots(mockSlots);
      }
    } catch (error) {
      console.error('Error loading slots:', error);
      // Fallback to mock slots
      const mockSlots = generateMockSlots();
      setAvailableSlots(mockSlots);
    } finally {
      setLoadingSlots(false);
    }
  };

  // Generate mock slots for fallback (10-14 days)
  const generateMockSlots = () => {
    const slots = [];
    const today = new Date();
    
    // Generate slots for next 10-14 days
    for (let i = 1; i <= 14; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      
      // Skip Sundays
      if (date.getDay() === 0) {
        continue;
      }
      
      // Generate time slots for each day
      const timeSlots = [
        '10:00 AM', '11:00 AM', '12:00 PM', '2:00 PM', '3:00 PM', '4:00 PM', '5:00 PM'
      ];
      
      timeSlots.forEach(time => {
        const slotDateTime = new Date(date);
        const [timeStr, period] = time.split(' ');
        const [hours, minutes] = timeStr.split(':');
        
        slotDateTime.setHours(
          period === 'PM' && hours !== '12' ? parseInt(hours) + 12 : 
          period === 'AM' && hours === '12' ? 0 : parseInt(hours),
          parseInt(minutes)
        );
        
        slots.push({
          id: `${date.toISOString().split('T')[0]}_${time}`,
          startTime: slotDateTime.toISOString(),
          endTime: new Date(slotDateTime.getTime() + 30 * 60000).toISOString(),
          date: date.toLocaleDateString('en-US', { 
            weekday: 'short', 
            month: 'short', 
            day: 'numeric' 
          }),
          time: time,
          available: true
        });
      });
    }
    
    return slots;
  };

  // Restore form data after login redirect
  useEffect(() => {
    const savedFormData = localStorage.getItem('consultationFormData');
    const wasFormSubmitted = localStorage.getItem('consultationFormSubmitted');
    
    if (savedFormData && wasFormSubmitted && currentUser) {
      try {
        const parsedData = JSON.parse(savedFormData);
        setFormData(parsedData);
        
        // Clear the stored data
        localStorage.removeItem('consultationFormData');
        localStorage.removeItem('consultationFormSubmitted');
        
        // Scroll to form section first, then auto-submit
        setTimeout(() => {
          scrollToForm();
          // Auto-submit the form to proceed to payment after scrolling
          setTimeout(() => {
            handleFormSubmission(parsedData);
          }, 500); // Wait for scroll to complete
        }, 100);
        
      } catch (error) {
        console.error('Error restoring form data:', error);
        localStorage.removeItem('consultationFormData');
        localStorage.removeItem('consultationFormSubmitted');
      }
    }
  }, [currentUser]);

  // Enhanced Calendly integration with event listeners
  useEffect(() => {
    // Listen for Calendly events
    window.addEventListener('message', handleCalendlyEvent);
    
    // Cleanup
    return () => {
      window.removeEventListener('message', handleCalendlyEvent);
    };
  }, []);



     useEffect(() => {
     if (showCalendlyModal && window.Calendly) {
       // Format phone number: if it's 10 digits and doesn't start with '+', add +91
       let phone = formData.phone || '';
       if (/^\d{10}$/.test(phone)) {
         phone = '+91' + phone;
       } else if (!phone.startsWith('+')) {
         // For other cases without country code, add +91 as fallback
         phone = '+91' + phone;
       }
       
       // Build Calendly URL with preselected date if available
       let calendlyUrl =
         formData.consultationType === 'video'
           ? 'https://calendly.com/vastrebyvijh/video-consultation-vastre-by-vijh'
           : `https://calendly.com/vastrebyvijh/phone-consultation?location=${encodeURIComponent(phone)}`;
       
       // Add preselected date if slot is selected
       if (selectedSlot) {
         const selectedDate = new Date(selectedSlot.startTime);
         const dateParam = selectedDate.toISOString().split('T')[0]; // YYYY-MM-DD format
         
         calendlyUrl += calendlyUrl.includes('?') ? '&' : '?';
         calendlyUrl += `date=${dateParam}`;
       }
       
       
       window.Calendly.initInlineWidget({
         url: calendlyUrl,
         parentElement: document.querySelector('.calendly-inline-widget'),
         prefill: {
           name: formData.name || '',
           email: formData.email || '',
           a3: `Consultation Type: ${formData.consultationType || ''}`,
           a4: formData.message || '',
           a5: selectedSlot ? `Preferred Date: ${selectedSlot.date} - Please select your preferred time slot on this date` : ''
         },
         utm: {
           utm_campaign: 'bespoke-consultation',
           utm_source: 'vastrebyvijh',
           utm_medium: 'website'
         }
       });
     }
   }, [
     showCalendlyModal,
     formData.name,
     formData.email,
     formData.phone,
     formData.consultationType,
     formData.message,
     selectedSlot
   ]);
  
  // Handle Calendly events
  const handleCalendlyEvent = (event) => {
    if (event.data.event && event.data.event.indexOf('calendly') === 0 && event.data.event === 'calendly.event_scheduled') {
      // Booking was successful
      const eventDetails = event.data.payload;
      
      // Update the booking with slot details
      updateBookingWithSlot(eventDetails);
    }
  };

  // Clear all states and reset form
  const clearAllStates = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      consultationType: '',
      slot: '',
      message: ''
    });
    setBookingId(null);
    setShowCalendlyModal(false);
    setShowSlotSuccessModal(false);
    setIsProcessing(false);
    setShowFullLoader(false);
    
    // Clear refs
    bookingIdRef.current = null;
    formDataRef.current = {};
  };

  // Update booking with slot details
  const updateBookingWithSlot = async (eventDetails) => {
    const currentBookingId = bookingIdRef.current || bookingId;
    const currentFormData = formDataRef.current || formData;
    
    if (!currentBookingId) {
      return;
    }

    try {
      // Extract event and invitee URIs
      const eventUri = eventDetails.event?.uri || '';
      const inviteeUri = eventDetails.invitee?.uri || '';
      
      const slotData = {
        bookingSlot: {
          eventUri: eventUri,
          eventName: '30 Minute Meeting',
          startTime: new Date(),
          endTime: new Date(),
          timezone: 'Asia/Kolkata',
          location: 'Web conferencing details to follow',
          inviteeName: currentFormData.name || '',
          inviteeEmail: currentFormData.email || '',
          inviteePhone: currentFormData.phone || '', // Add phone number
          bookingUrl: inviteeUri,
          createdAt: new Date()
        },
        status: 'scheduled',
        updatedAt: new Date()
      };

      // Update the document
      const docRef = doc(db, 'consultations', currentBookingId);
      await updateDoc(docRef, slotData);
      
      // Show success message and close modal
      setShowCalendlyModal(false);
      setShowSlotSuccessModal(true);
      
      // Clear all states after showing success
      setTimeout(() => {
        clearAllStates();
      }, 8000); // Auto clear after 8 seconds
      
    } catch (error) {
      alert('Slot booked but there was an error updating your booking. Please contact support.');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // If consultation type is selected, fetch available slots
    if (name === 'consultationType' && value) {
      // Small delay to ensure state is updated
      setTimeout(() => {
        fetchAvailableSlots();
      }, 100);
    }
  };

  // Date navigation functions
  const navigateDate = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + direction);
    
    // Don't allow going to past dates or beyond 7 days
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const maxDate = new Date();
    maxDate.setDate(maxDate.getDate() + 7);
    
    if (newDate >= today && newDate <= maxDate) {
      setCurrentDate(newDate);
    }
  };

  const handleDateChange = (event) => {
    const selectedDate = new Date(event.target.value);
    
    // Don't allow selecting previous dates or beyond 7 days
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const maxDate = new Date();
    maxDate.setDate(maxDate.getDate() + 7);
    
    if (selectedDate >= today && selectedDate <= maxDate) {
      setCurrentDate(selectedDate);
    }
  };

  const formatDateForDisplay = (date) => {
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: '2-digit'
    });
  };

  const formatDateForInput = (date) => {
    return date.toISOString().split('T')[0];
  };




    



  const scrollToForm = () => {
    const formElement = document.getElementById('consultation-form');
    if (formElement) {
      formElement.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start' 
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!currentUser) {
      // Store form data in localStorage before redirecting
      localStorage.setItem('consultationFormData', JSON.stringify(formData));
      localStorage.setItem('consultationFormSubmitted', 'true');
      
      // Redirect directly to login without alert
      const loginUrl = `/login?returnUrl=${encodeURIComponent('/bespoke-consultation')}`;
      navigate(loginUrl);
      return;
    }

    // If user is logged in, proceed with form submission
    await handleFormSubmission();
  };

  // Extract form submission logic into separate function
  const handleFormSubmission = async (dataToSubmit = formData) => {
    // Validate required fields
    if (!dataToSubmit.name || !dataToSubmit.email || !dataToSubmit.phone || !dataToSubmit.consultationType) {
      // Show beautiful error message instead of alert
      setValidationError('Please fill in all required fields: Name, Email, Phone, and Consultation Type.');
      
      // Scroll to the form section
      const formSection = document.getElementById('consultation-form');
      if (formSection) {
        formSection.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start' 
        });
      }
      
      // Clear error after 5 seconds
      setTimeout(() => {
        setValidationError(null);
      }, 5000);
      
      return;
    }

    setIsProcessing(true);

    try {
      // Determine amount based on consultation type
      const amount = dataToSubmit.consultationType === 'video' ? 999 : 799; // ₹999 for video, ₹799 for phone
      
      // Prepare booking data
      const bookingData = {
        userId: currentUser.uid,
        userName: dataToSubmit.name,
        userEmail: dataToSubmit.email,
        userPhone: dataToSubmit.phone,
        consultationType: dataToSubmit.consultationType,
        bookingSlot: dataToSubmit.slot,
        additionalNotes: dataToSubmit.message,
        amount: amount,
        status: 'pending',
        createdAt: new Date(),
        paymentStatus: 'pending',
        razorpayOrderId: null,
        razorpayPaymentId: null
      };

      // Create booking in Firestore BEFORE payment
      const bookingRef = await addDoc(collection(db, 'consultations'), bookingData);
      const bookingId = bookingRef.id;
      
      
      // Store booking ID and form data in refs immediately
      setBookingId(bookingId);
      bookingIdRef.current = bookingId;
      formDataRef.current = { ...dataToSubmit };

      // Create Razorpay order using Firebase function
      const receipt = `consultation_${bookingId}`;
      const token = await currentUser.getIdToken();
      
      const razorpayOrderData = {
        amount: amount,
        currency: "INR",
        receipt: receipt,
      };

      const response = await fetch(
        "https://asia-south1-vastrebyvijh-8c454.cloudfunctions.net/createRazorpayOrder",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(razorpayOrderData),
        }
      );

      const razorpayOrderResult = await response.json();

      if (!razorpayOrderResult.success) {
        throw new Error(
          razorpayOrderResult.error || "Failed to create Razorpay order"
        );
      }

      const razorpayOrder = razorpayOrderResult.order || {};
      const key_id = razorpayOrder.key_id || razorpayOrderResult.key_id || "";

      if (!razorpayOrder.id) {
        throw new Error("Invalid Razorpay order response - missing order ID");
      }

      const options = {
        key: key_id,
        amount: razorpayOrder.amount,
        currency: razorpayOrder.currency,
        name: "Vastré by Vijh",
        description: `${formData.consultationType === 'video' ? 'Video' : 'Phone'} Consultation Booking`,
        order_id: razorpayOrder.id,
        image: "/images/monogram/monogram_desktop_1756135549612_0.webp",
        handler: function (response) {
          // Show loader immediately after Razorpay success
          setShowFullLoader(true);
          handlePaymentSuccess(response, bookingId, razorpayOrder.id);
        },
        prefill: {
          name: formData.name,
          email: formData.email,
          contact: formData.phone
        },
        notes: {
          consultationType: formData.consultationType,
          razorpayOrderId: razorpayOrder.id,
          bookingId: bookingId,
        },
        theme: {
          color: "#3B0618",
          hide_topbar: true,
        },
        modal: {
          ondismiss: function () {
            setIsProcessing(false);
          },
        },
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();

    } catch (error) {
      alert(`Booking initialization failed: ${error.message}. Please try again.`);
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle payment success
  const handlePaymentSuccess = async (response, bookingId, razorpayOrderId) => {
    try {
      // Verify payment signature
      const token = await currentUser.getIdToken();
      
      const verificationData = {
        razorpay_order_id: razorpayOrderId,
        razorpay_payment_id: response.razorpay_payment_id,
        razorpay_signature: response.razorpay_signature,
      };

      const verificationResponse = await fetch(
        "https://asia-south1-vastrebyvijh-8c454.cloudfunctions.net/verifyPayment",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(verificationData),
        }
      );

      const verificationResult = await verificationResponse.json();

      if (!verificationResult.success) {
        throw new Error(verificationResult.error || "Payment verification failed");
      }

      // Update existing booking with payment details
      const paymentUpdateData = {
        status: 'payment_completed',
        paymentStatus: 'completed',
        razorpayOrderId: razorpayOrderId,
        razorpayPaymentId: response.razorpay_payment_id,
        paidAt: new Date(),
        updatedAt: new Date()
      };

      const docRef = doc(db, 'consultations', bookingId);
      await updateDoc(docRef, paymentUpdateData);
      
      // Automatically open Calendly after payment success (reduced delay)
      setTimeout(() => {
        setShowFullLoader(false);
        setShowCalendlyModal(true);
      }, 1000); // Reduced to 1 second for smoother transition

    } catch (error) {
      alert(`Payment verification failed: ${error.message}. Please contact support.`);
    }
  };



  return (
    <>
      {/* Full Page Loader */}
      <Loader isLoading={showFullLoader} />
      
      <div className="bespoke-consultation-page">
      {/* Hero Section */}
      <section className="consultation-hero">
        <div className="bespoke-hero-content">
          <div className="bespoke-hero-label">
            <div className="bespoke-hero-label-line"></div>
            <span className="bespoke-hero-label-text">Premium Styling Service</span>
            <div className="bespoke-hero-label-line"></div>
          </div>
          <h1>Bespoke Consultation</h1>
          <p>Schedule a personalized video or phone consultation with our expert stylist for a bespoke fashion experience tailored to your needs. Professional styling advice and personalized recommendations.</p>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="pricing-section">
        <div className="pricing-container">
          <h2>Consultation Pricing</h2>
          <div className="pricing-cards">
            <div className="pricing-card">
              <div className="pricing-header">
                <h3>Video Consultation</h3>
                <div className="price">₹999</div>
                <p className="duration">30 minutes</p>
              </div>
              <ul className="pricing-features">
                <li>Personalized style assessment</li>
                <li>Body type analysis</li>
                <li>Wardrobe recommendations</li>
                <li>Style guide PDF</li>
              </ul>
              <div className="pricing-cta">
                <button type="button" onClick={() => {
                  setFormData(prev => ({ ...prev, consultationType: 'video' }));
                  scrollToForm();
                }}>
                  Book Video Consultation
                </button>
              </div>
            </div>
            <div className="pricing-card featured">
              <div className="pricing-header">
                <h3>Phone Consultation</h3>
                <div className="price">₹799</div>
                <p className="duration">30 minutes</p>
              </div>
              <ul className="pricing-features">
                <li>Style consultation</li>
                <li>Basic recommendations</li>
                <li>Follow-up email</li>
                <li>Style tips</li>
              </ul>
              <div className="pricing-cta">
                <button type="button" onClick={() => {
                  setFormData(prev => ({ ...prev, consultationType: 'phone' }));
                  scrollToForm();
                }}>
                  Book Phone Consultation
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Consultation Form Section */}
      <section id="consultation-form" className="consultation-form-section">
        <div className="form-container">
          <div className="bespoke-form-header">
            <h2>Book Your Consultation</h2>
            <p>Select your consultation type and schedule a time that works for you.</p>
          </div>
          
          {/* Validation Error Display */}
          {validationError && (
            <div className="validation-error-message">
              <div className="error-icon">⚠️</div>
              <div className="error-text">{validationError}</div>
              <button 
                className="error-close-btn"
                onClick={() => setValidationError(null)}
                aria-label="Close error message"
              >
                ×
              </button>
            </div>
          )}
          
          <form className="consultation-form" onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="name">Full Name *</label>
                <input 
                  type="text" 
                  id="name" 
                  name="name" 
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Enter your full name"
                  required 
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="email">Email Address *</label>
                <input 
                  type="email" 
                  id="email" 
                  name="email" 
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Enter your email address"
                  required 
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="consultationType">Consultation Type *</label>
                <select 
                  id="consultationType" 
                  name="consultationType" 
                  value={formData.consultationType}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select consultation type</option>
                  <option value="phone">Phone Consultation</option>
                  <option value="video">Video Conferencing</option>
                </select>
              </div>
              
              <div className="form-group">
                <label htmlFor="phone">Phone Number *</label>
                <input 
                  type="tel" 
                  id="phone" 
                  name="phone" 
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="Enter your phone number"
                  required
                />
              </div>
            </div>

                         <div className="form-row">
               <div className="form-group">
                 <label htmlFor="slot">Consultation Slot</label>
                 {formData.consultationType ? (
                   <div className="slot-selection-container">
                     {selectedSlot ? (
                       <div className="selected-slot">
                         <FaCalendarAlt className="calendar-icon" />
                         <span>{selectedSlot.date} at {selectedSlot.time}</span>
                         <button 
                           type="button" 
                           className="change-slot-btn"
                           onClick={() => setShowSlotSelection(true)}
                         >
                           Change
                         </button>
                       </div>
                     ) : (
                       <button 
                         type="button" 
                         className="custom-view-slots-btn"
                         onClick={() => {
                           if (availableSlots.length === 0) {
                             fetchAvailableSlots();
                           }
                           setShowSlotSelection(true);
                         }}
                         disabled={loadingSlots}
                       >
                         {loadingSlots ? 'Finding slots...' : 'View Available Slots'}
                       </button>
                     )}
                   </div>
                 ) : (
                   <div className="calendly-placeholder">
                     <FaCalendarAlt className="calendar-icon" />
                     <span>Select consultation type first</span>
                   </div>
                 )}
               </div>
             </div>

            <div className="form-group full-width">
              <label htmlFor="message">Additional Notes</label>
              <textarea 
                id="message" 
                name="message" 
                value={formData.message}
                onChange={handleInputChange}
                placeholder="Tell us about your style preferences, occasion, or any specific requirements..."
                rows="4"
              ></textarea>
            </div>
            
            <button type="submit" className="book-now-btn" disabled={isProcessing}>
              {isProcessing ? 'Processing Payment...' : 'Pay & Book Consultation'}
            </button>
          </form>
        </div>
      </section>

      {/* Consultation Benefits Section */}
      <section className="consultation-benefits">
        <div className="benefits-container">
          <h2>Why Choose Our Bespoke Consultation?</h2>
          <div className="benefits-grid">
            <div className="benefit-card">
              <div className="benefit-icon">
                <FaBullseye />
              </div>
              <h3>Personalized Approach</h3>
              <p>Every consultation is tailored to your unique style, body type, and preferences.</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon">
                <FaUserTie />
              </div>
              <h3>Expert Styling</h3>
              <p>Get advice from our experienced stylists who understand premium fashion and fit.</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon">
                <FaMobileAlt />
              </div>
              <h3>Convenient Booking</h3>
              <p>Schedule consultations at your convenience via video call or phone.</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon">
                <FaStar />
              </div>
              <h3>Premium Experience</h3>
              <p>Enjoy a premium consultation experience that matches our high-quality brand standards.</p>
            </div>
          </div>
        </div>
      </section>





             {/* Compact Slot Selection Modal */}
       {showSlotSelection && (
         <div className="custom-slot-selection-modal-overlay">
           <div className="custom-slot-selection-modal">
             <div className="custom-slot-selection-modal-header">
               <h3>Available Time Slots</h3>
               <p className="custom-slot-selection-info">
                 These are the available time slots you can book once payment is done.
               </p>
               <button 
                 className="custom-close-button"
                 onClick={() => setShowSlotSelection(false)}
               >
                 <FaTimes />
               </button>
             </div>
             <div className="custom-slot-selection-modal-content">
               {/* Date Navigation */}
               <div className="custom-date-navigation">
                 <button 
                   className="custom-date-nav-btn"
                   onClick={() => navigateDate(-1)}
                   disabled={loadingSlots || currentDate <= new Date(new Date().setHours(0, 0, 0, 0))}
                 >
                   <FaChevronLeft />
                 </button>
                 
                 <input
                   type="date"
                   className="custom-date-picker"
                   value={formatDateForInput(currentDate)}
                   onChange={handleDateChange}
                   min={formatDateForInput(new Date())}
                   max={formatDateForInput(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000))}
                 />
                 
                 <button 
                   className="custom-date-nav-btn"
                   onClick={() => navigateDate(1)}
                   disabled={loadingSlots || currentDate >= new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)}
                 >
                   <FaChevronRight />
                 </button>
               </div>

               {loadingSlots ? (
                 <div className="custom-loading-slots">
                   <div className="custom-elegant-loader">
                     <div className="custom-loader-ring"></div>
                     <div className="custom-loader-ring"></div>
                     <div className="custom-loader-ring"></div>
                   </div>
                   <p className="custom-loading-text">Discovering your perfect time slots...</p>
                 </div>
               ) : (
                 <div>
                   {(() => {
                     // Filter slots for the selected date only
                     const selectedDateStr = currentDate.toLocaleDateString('en-US', { 
                       weekday: 'short', 
                       month: 'short', 
                       day: 'numeric' 
                     });
                     
                     const slotsForDate = availableSlots.filter(slot => slot.date === selectedDateStr);
                     
                     if (slotsForDate.length === 0) {
                       return (
                         <div className="custom-no-slots">
                           <p>No slots available for this date.</p>
                         </div>
                       );
                     }
                     
                     return (
                       <div className="custom-date-group">
                         <div className="custom-date-slots-grid">
                           {slotsForDate.map((slot) => (
                             <div 
                               key={slot.id}
                               className={`custom-compact-slot-card custom-display-only ${slot.available ? 'custom-available' : 'custom-unavailable'}`}
                             >
                               <div className="custom-compact-slot-time">
                                 <FaClock /> {slot.time}
                               </div>
                               <div className="custom-compact-slot-status">
                                 {slot.available ? 'Available' : 'Booked'}
                               </div>
                             </div>
                           ))}
                         </div>
                       </div>
                     );
                   })()}
                 </div>
               )}
             </div>
             <div className="custom-slot-selection-modal-footer">
               <button 
                 className="custom-continue-to-payment-btn"
                 onClick={async () => {
                   setShowSlotSelection(false);
                   // Call the same function as Pay & Book Consultation
                   await handleSubmit({ preventDefault: () => {} });
                 }}
               >
                 Continue to Payment
               </button>
             </div>
           </div>
         </div>
       )}

       {/* Calendly Modal */}
       {showCalendlyModal && (
         <div className="calendly-modal-overlay">
           <div className="calendly-modal">
             <div className="calendly-modal-header">
               <h3>Confirm Your Consultation Time</h3>
               <button 
                 className="close-button"
                 onClick={() => setShowCalendlyModal(false)}
               >
                 <FaTimes />
               </button>
             </div>
             <div className="calendly-modal-content">
               <div 
                 className="calendly-inline-widget" 
                 style={{ minWidth: '320px', height: '600px' }}
               />
             </div>
           </div>
         </div>
       )}

      {/* Slot Booking Success Modal */}
      {showSlotSuccessModal && (
        <div className="success-modal-overlay">
          <div className="success-modal">
            <div className="success-modal-header">
              <FaCheckCircle className="success-icon" />
              <h3>Consultation Booked Successfully!</h3>
            </div>
            <div className="success-modal-content">
              <p>Your consultation slot has been confirmed. You will receive a calendar invitation and confirmation email shortly.</p>
              <div className="success-modal-actions">
                <button 
                  className="btn-primary"
                  onClick={clearAllStates}
                >
                  Continue
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </>
  );
};

export default BespokeConsultation;